import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Layout components
import HeaderSimple from './components/layout/HeaderSimple';
import SecondaryNav from './components/layout/SecondaryNav';
import SkipLink from './components/accessibility/SkipLink';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/layout/Footer';
import CookieConsent from './components/gdpr/CookieConsent';
import SuiviPages from './components/SuiviPages';
import Sidebar from './components/layout/Sidebar';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Profile from './pages/Profile';
import Instructor from './pages/Instructor';
import Admin from './pages/Admin';
import UsersAdmin from './pages/Users';
import Reports from './pages/Reports';
import AdminCertificates from './pages/AdminCertificates';
import CertificateSettings from './pages/CertificateSettings';
import Learn from './pages/Learn';
import CreateCourse from './pages/CreateCourse';
import EditCourse from './pages/EditCourse';
import CourseContentManager from './pages/CourseContentManager';
import About from './pages/About';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import Evaluations from './pages/Evaluations';
import TakeQuiz from './pages/TakeQuiz';
import Subscription from './pages/Subscription';
import Chat from './pages/Chat';
import AIAssistant from './pages/AIAssistant';
import Certificates from './pages/Certificates';
import VerifyCertificate from './pages/VerifyCertificate';
import CertificateViewer from './components/certificate/CertificateViewer';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Reviews from './pages/Reviews';
import Programs from './pages/Programs';
import Gamification from './pages/Gamification';
import Documentation from './pages/Documentation';
import MyCourses from './pages/MyCourses';
import CourseViewer from './components/course/CourseViewer';
import SimpleContentEditor from './components/course/SimpleContentEditor';
import CourseEnrollment from './components/course/CourseEnrollment';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { EvaluationProvider } from './contexts/EvaluationContext';
import { ChatProvider } from './contexts/ChatContext';
import { CertificateProvider } from './contexts/CertificateContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ReviewProvider } from './contexts/ReviewContext';
import { RecommendationProvider } from './contexts/RecommendationContext';
import { ProgramProvider } from './contexts/ProgramContext';
import { GamificationProvider } from './contexts/GamificationContext';
import { CourseContentProvider } from './contexts/CourseContentContext';
import { EnrollmentProvider } from './contexts/EnrollmentContext';
import { CourseCreationProvider } from './contexts/CourseCreationContext';
import { CoachingProvider } from './contexts/CoachingContext';
import Coaching from './pages/Coaching';
import AccompanimentPlan from './pages/coaching/AccompanimentPlan';
import CoachingMinutes from './pages/coaching/CoachingMinutes';
import CoachingDashboard from './pages/coaching/CoachingDashboard';
import Diagnostic from './pages/coaching/Diagnostic';

// Protected route components
import ProtectedRoute from './components/auth/ProtectedRoute';
import InstructorRoute from './components/auth/InstructorRoute';
import AdminRoute from './components/auth/AdminRoute';

const AuthDebug = process.env.NODE_ENV === 'development'
  ? require('./components/debug/AuthDebug').default
  : null;

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <CurrencyProvider>
            <ChatProvider>
              <CertificateProvider>
                <AnalyticsProvider>
                  <NotificationProvider>
                    <ReviewProvider>
                      <RecommendationProvider>
                        <ProgramProvider>
                          <GamificationProvider>
                            <CourseContentProvider>
                              <EnrollmentProvider>
                                <CourseCreationProvider>
                                  <CoachingProvider>
                                  <EvaluationProvider>
                                    <CourseProvider>
          <Router>
            <SuiviPages />
            <div className="min-h-screen bg-background dark:bg-dark-background flex flex-col">
              <SkipLink />
              <HeaderSimple />
              <SecondaryNav />
              
              <main className="flex-1 flex overflow-clip">
                <div className="hidden md:block flex-shrink-0">
                  <Sidebar />
                </div>
                <div id="main-content" className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6" tabIndex="-1">
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/courses/:slug" element={<CourseDetail />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/terms" element={<TermsOfUse />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/verify" element={<VerifyCertificate />} />
                    <Route path="/documentation" element={<Documentation />} />
                    
                    {/* Protected routes */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/settings" 
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      } 
                    />
                                                <Route 
                              path="/learn/:courseSlug" 
                              element={
                                <ProtectedRoute>
                                  <Learn />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/course/:courseId/evaluations" 
                              element={
                                <ProtectedRoute>
                                  <Evaluations />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/evaluation/:evaluationId/take" 
                              element={
                                <ProtectedRoute>
                                  <TakeQuiz />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/subscription" 
                              element={
                                <ProtectedRoute>
                                  <Subscription />
                                </ProtectedRoute>
                              } 
                            />
                            <Route
                              path="/chat"
                              element={
                                <ProtectedRoute>
                                  <Chat />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/ai-assistant"
                              element={
                                <ProtectedRoute>
                                  <AIAssistant />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/certificates" 
                              element={
                                <ProtectedRoute>
                                  <Certificates />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/certificates/:certificateId" 
                              element={
                                <ProtectedRoute>
                                  <CertificateViewer />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/analytics" 
                              element={
                                <ProtectedRoute>
                                  <Analytics />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/notifications" 
                              element={
                                <ProtectedRoute>
                                  <Notifications />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/reviews" 
                              element={
                                <ProtectedRoute>
                                  <Reviews />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/programs" 
                              element={
                                <ProtectedRoute>
                                  <Programs />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/gamification" 
                              element={
                                <ProtectedRoute>
                                  <Gamification />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/my-courses" 
                              element={
                                <ProtectedRoute>
                                  <MyCourses />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/course/:courseId" 
                              element={
                                <ProtectedRoute>
                                  <CourseEnrollment />
                                </ProtectedRoute>
                              } 
                            />
                            <Route 
                              path="/course/:courseId/learn" 
                              element={
                                <ProtectedRoute>
                                  <CourseViewer />
                                </ProtectedRoute>
                              } 
                            />
                            <Route
                              path="/instructor/course/:courseId/content/create"
                              element={
                                <InstructorRoute>
                                  <SimpleContentEditor />
                                </InstructorRoute>
                              }
                            />
                            <Route
                              path="/instructor/course/:courseId/manage"
                              element={
                                <InstructorRoute>
                                  <CourseContentManager />
                                </InstructorRoute>
                              }
                            />
                    
                    {/* Instructor routes */}
                    <Route 
                      path="/instructor" 
                      element={
                        <InstructorRoute>
                          <Instructor />
                        </InstructorRoute>
                      } 
                    />
                    <Route
                      path="/instructor/create-course"
                      element={
                        <InstructorRoute>
                          <CreateCourse />
                        </InstructorRoute>
                      }
                    />
                    <Route
                      path="/instructor/course/:courseId/edit"
                      element={
                        <InstructorRoute>
                          <EditCourse />
                        </InstructorRoute>
                      }
                    />
                    
                    {/* Coaching routes */}
                    <Route
                      path="/coaching"
                      element={
                        <ProtectedRoute>
                          <Coaching />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/coaching/plan"
                      element={
                        <ProtectedRoute>
                          <AccompanimentPlan />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/coaching/minutes"
                      element={
                        <ProtectedRoute>
                          <CoachingMinutes />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/coaching/dashboard"
                      element={
                        <ProtectedRoute>
                          <CoachingDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/coaching/diagnostic"
                      element={
                        <ProtectedRoute>
                          <Diagnostic />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin routes */}
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <Admin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <AdminRoute>
                          <UsersAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <AdminRoute>
                          <Reports />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/certificates"
                      element={
                        <AdminRoute>
                          <AdminCertificates />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/certificate-settings"
                      element={
                        <AdminRoute>
                          <CertificateSettings />
                        </AdminRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </main>
              
              <Footer />
              <CookieConsent />
            </div>

            {/* Debug component - affiché uniquement en développement */}
            {AuthDebug && <AuthDebug />}
          </Router>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
                  </CourseProvider>
                    </EvaluationProvider>
                  </CoachingProvider>
                  </CourseCreationProvider>
                </EnrollmentProvider>
              </CourseContentProvider>
            </GamificationProvider>
          </ProgramProvider>
        </RecommendationProvider>
      </ReviewProvider>
    </NotificationProvider>
  </AnalyticsProvider>
</CertificateProvider>
            </ChatProvider>
</CurrencyProvider>
</AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
